import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Share2, Copy, X, MessageCircle, ArrowLeft } from "lucide-react";
import LinkolaMark from "./LinkolaMark";
import { toast } from "sonner";
import { api } from "../lib/api";
import { openExternalUrl, isNativeCapacitor } from "../lib/openExternal";

/**
 * Floating Share + Join CTA icons — pinned to the top-left / top-right of
 * every public Action Page and the Creator's main /u/{slug} bio hub.
 * Mirrors the Linktree "share sheet + brand mark" pattern shown in the
 * reference screenshots.
 *
 * Props:
 *   creatorSlug — required. Used to build the canonical share URL and to
 *     record a share event.
 *   pageSlug    — optional. When present (individual action page), the
 *     share event is recorded against `/public/pages/{slug}/{page}/share`.
 *     When absent (main bio hub), we skip the per-page share ping and
 *     record against `/public/creators/{slug}/share` instead (fire-and-
 *     forget; endpoint is optional).
 *   pageTitle   — used inside the native share sheet.
 */
export default function PublicPageOverlay({ creatorSlug, pageSlug, pageTitle }) {
  const [shareOpen, setShareOpen] = useState(false);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const navigate = useNavigate();
  // v146.9 — This overlay was designed for a REAL external browser tab
  // (web new-tab, or SFSafariViewController/Custom Tab on native), which
  // always supplies its own safe-area handling and its own close/back
  // button. It's now also reachable via in-app navigation (the iOS
  // cleartext-URL fallback in lib/openExternal.js routes local-build "See
  // it live" taps here directly, since Safari won't load a plain http://
  // URL at all) — a context this component was never built for: the
  // fixed-top icons collided with the status bar / Dynamic Island, and
  // there was no way back to the app at all. Both fixed here, for ANY
  // native in-app rendering of this component, not just that one path
  // (e.g. a universal link opened while the app is already running would
  // hit the same problem).
  const native = isNativeCapacitor();
  const canGoBack = native && typeof window !== "undefined" && window.history.length > 1;
  // `top-3`/`sm:top-4`'s pixel value (0.75rem), plus the safe-area inset —
  // `.top-safe` (index.css) alone would sit flush against the notch with
  // no breathing room, so this adds to it rather than replacing it.
  const topSafeStyle = { top: "calc(0.75rem + env(safe-area-inset-top, 0px))" };
  // v146.7 — The back/join, brand-name, and share pills only ever painted
  // their own small rounded backgrounds, not a full-width bar. Everything
  // in the gaps between them — and the entire safe-area/status-bar strip
  // above them — was transparent, so scrolled page content (blocks,
  // avatars, text) slid up underneath and showed through those gaps,
  // reading as a broken/overlapping header instead of a solid native app
  // bar. This band sits behind (z-30 vs the pills' z-40) and fully covers
  // the safe-area inset + the pill row + a little breathing room below it,
  // so scrolled content disappears behind a solid bar instead of bleeding
  // through. Height must stay in sync with `topSafeStyle`'s top offset
  // (0.75rem) + the pills' height (h-11 = 2.75rem) + bottom breathing room.
  const barHeightStyle = { height: "calc(env(safe-area-inset-top, 0px) + 3.75rem)" };

  const recordShare = async (channel) => {
    try {
      const path = pageSlug
        ? `/public/pages/${creatorSlug}/${pageSlug}/share`
        : `/public/creators/${creatorSlug}/share`;
      await api.post(path, { channel });
    } catch (_e) { /* non-blocking */ }
  };

  const doNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: pageTitle || "Check this out", url: shareUrl });
        recordShare("native");
        setShareOpen(false);
        return;
      } catch (_e) {
        // user cancelled — fall through to sheet
      }
    }
    setShareOpen(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      recordShare("copy");
      toast.success("Link copied");
      setShareOpen(false);
    } catch (_e) {
      toast.error("Copy failed — long-press to copy manually.");
    }
  };

  const shareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(pageTitle || "")}`;
    openExternalUrl(url);
    recordShare("twitter");
    setShareOpen(false);
  };
  const shareWhatsApp = () => {
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent((pageTitle ? pageTitle + " — " : "") + shareUrl)}`;
    openExternalUrl(url);
    recordShare("whatsapp");
    setShareOpen(false);
  };
  const shareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    openExternalUrl(url);
    recordShare("facebook");
    setShareOpen(false);
  };
  const shareTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(pageTitle || "")}`;
    openExternalUrl(url);
    recordShare("telegram");
    setShareOpen(false);
  };

  return (
    <>
      {/* Solid app-bar background — sits behind the back/name/share pills
          and spans the full width + safe-area/status-bar strip above them.
          Without this, the pills' own backgrounds only cover themselves,
          so scrolled content shows through the gaps between them. */}
      <div
        aria-hidden="true"
        data-testid="public-overlay-topbar-bg"
        className="fixed top-0 left-0 right-0 z-30 bg-white border-b-2 border-ink"
        style={barHeightStyle}
      />

      {/* Left — Back (native, when there's app history to return to) or
          Join Linkola (web, or a cold native launch straight into this
          page with nothing to go back to). */}
      {canGoBack ? (
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="public-overlay-back"
          aria-label="Back"
          title="Back"
          style={topSafeStyle}
          className="fixed left-3 sm:left-4 z-40 w-11 h-11 rounded-2xl bg-white border-2 border-ink shadow-brutalSm flex items-center justify-center text-ink hover:bg-sun hover:translate-y-[1px] transition-all"
        >
          <ArrowLeft className="w-[22px] h-[22px]" strokeWidth={2.5} />
        </button>
      ) : (
        <Link
          to="/join"
          data-testid="public-overlay-join"
          aria-label="Join Linkola"
          title="Join Linkola"
          onClick={() => {
            // v145.34 — fire-and-forget click tracking. Powers the
            // Super Admin "Linkola clicks" column so we know which
            // creator pages send the most traffic back to /join.
            if (creatorSlug) {
              api.post(`/public/creators/${creatorSlug}/linkola-click`).catch(() => {});
            }
          }}
          style={topSafeStyle}
          className="fixed left-3 sm:left-4 z-40 w-11 h-11 rounded-2xl bg-white border-2 border-ink shadow-brutalSm flex items-center justify-center text-ink hover:bg-sun hover:translate-y-[1px] transition-all"
        >
          <LinkolaMark size={22} strokeWidth={3} />
        </Link>
      )}

      {/* Right — Share */}
      <button
        type="button"
        onClick={doNativeShare}
        data-testid="public-overlay-share-btn"
        aria-label="Share this page"
        title="Share"
        style={topSafeStyle}
        className="fixed right-3 sm:right-4 z-40 w-11 h-11 rounded-2xl bg-white border-2 border-ink shadow-brutalSm flex items-center justify-center text-ink hover:bg-sun hover:translate-y-[1px] transition-all"
      >
        <Share2 className="w-[22px] h-[22px]" strokeWidth={2.5} />
      </button>

      {/* Centre — Brand name pill (fills the gap between the Linkola icon
          and the Share button). Truncates on tiny screens; tapping opens
          the same share sheet since it doubles as a page identifier. */}
      {pageTitle && (
        <div
          className="fixed z-40 pointer-events-none"
          style={{ ...topSafeStyle, left: "3.75rem", right: "3.75rem" }}
          data-testid="public-overlay-brand-name"
        >
          <div className="max-w-full mx-auto flex justify-center">
            <div className="pointer-events-auto max-w-[85vw] px-3 py-2 bg-white border-2 border-ink shadow-brutalSm rounded-2xl">
              <p className="font-display font-black text-sm tracking-tight text-ink truncate">
                {pageTitle}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Fallback share sheet — shown when navigator.share isn't available */}
      {shareOpen && (
        <div
          className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/50 p-4"
          style={{ zIndex: "var(--ux-z-modal)" }}
          onClick={() => setShareOpen(false)}
          data-testid="public-overlay-share-sheet"
        >
          <div
            className="w-full max-w-sm bg-white border-2 border-ink shadow-brutal p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="font-display font-black text-lg tracking-tighter">Share this page</p>
              <button onClick={() => setShareOpen(false)} className="p-1" aria-label="Close" data-testid="public-overlay-share-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-4">
              <ShareBtn onClick={shareWhatsApp} label="WhatsApp" tone="bg-mint" testid="share-whatsapp">
                <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
              </ShareBtn>
              <ShareBtn onClick={shareTwitter} label="X" tone="bg-page" testid="share-twitter">
                <span className="font-black text-lg">𝕏</span>
              </ShareBtn>
              <ShareBtn onClick={shareFacebook} label="Facebook" tone="bg-lavender" testid="share-facebook">
                <span className="font-black text-lg">f</span>
              </ShareBtn>
              <ShareBtn onClick={shareTelegram} label="Telegram" tone="bg-bubble" testid="share-telegram">
                <span className="font-black text-lg">✈</span>
              </ShareBtn>
            </div>
            <button
              onClick={copyLink}
              data-testid="share-copy-link"
              className="w-full border-2 border-ink bg-cream px-3 py-3 font-bold text-sm inline-flex items-center gap-2 justify-center hover:bg-sun"
            >
              <Copy className="w-4 h-4" /> Copy link
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const ShareBtn = ({ onClick, label, tone, testid, children }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex flex-col items-center gap-1 border-2 border-ink py-3 hover:translate-y-[1px] ${tone}`}
  >
    {children}
    <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
  </button>
);
